import { HttpResponse } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { ReportSdk } from 'lib-klab-sdks';

import { DeviceFleetPort } from '../../../app/ports/reports/device-fleet.port';
import {
    DeviceFleetEmployeeResponse,
    DeviceFleetResponse,
} from '../../../domain/models/reports/device-fleet.model';
import {
    DeviceFleetExportRequest,
    DeviceFleetRequest,
} from '../../../domain/models/reports/request/device-fleet.request';
import { AdminExportFormat } from '../../../domain/models/reports/request/admin-export.request';
import { AppEventsService } from '../../events/products-events.service';
import {
    mapSdkDeviceFleetEmployeeResponseToDomain,
    mapSdkDeviceFleetResponseToDomain,
} from '../../mappers/reports/device-fleet.mapper';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';

/**
 * El Accept correcto por formato. Sin esto el SDK pide application/json y el
 * HttpClient intenta parsear el xlsx como JSON: la descarga sale corrupta.
 */
function acceptFor(format: AdminExportFormat): string {
    switch (String(format).toUpperCase()) {
        case String(AdminExportFormat.Csv):
            return 'text/csv, */*;q=0.8';
        case String(AdminExportFormat.Pdf):
            return 'application/pdf, */*;q=0.8';
        case String(AdminExportFormat.Excel):
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, */*;q=0.8';
        default:
            return '*/*';
    }
}

@Injectable({ providedIn: 'root' })
export class DeviceFleetPortAdapter implements DeviceFleetPort {
    constructor(
        private adminService: ReportSdk.AdminService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
    ) { }

    getDeviceFleet(payload: DeviceFleetRequest): Observable<DeviceFleetResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-devices');

        const sdkPayload: ReportSdk.AdminDeviceRequest = {
            ...(payload.employeeId != null ? { employeeId: payload.employeeId } : {}),
            ...(payload.search ? { search: payload.search } : {}),
            ...(payload.agentVersion ? { agentVersion: payload.agentVersion } : {}),
            ...(payload.deviceName ? { deviceName: payload.deviceName } : {}),
            ...(payload.includeDeleted != null ? { includeDeleted: payload.includeDeleted } : {}),
            ...(payload.page != null ? { page: payload.page } : {}),
            ...(payload.size != null ? { size: payload.size } : {}),
        };

        return this.adminService.getAdminDeviceFleet(sdkPayload).pipe(
            map((resp) => mapSdkDeviceFleetResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-devices')),
        );
    }

    getDeviceFleetByEmployee(payload: DeviceFleetRequest): Observable<DeviceFleetEmployeeResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-devices-by-employee');

        const sdkPayload: ReportSdk.AdminDeviceRequest = {
            ...(payload.employeeId != null ? { employeeId: payload.employeeId } : {}),
            ...(payload.search ? { search: payload.search } : {}),
            ...(payload.agentVersion ? { agentVersion: payload.agentVersion } : {}),
            ...(payload.deviceName ? { deviceName: payload.deviceName } : {}),
            ...(payload.includeDeleted != null ? { includeDeleted: payload.includeDeleted } : {}),
            ...(payload.page != null ? { page: payload.page } : {}),
            ...(payload.size != null ? { size: payload.size } : {}),
        };

        return this.adminService.getAdminDeviceFleetByEmployee(sdkPayload).pipe(
            map((resp) => mapSdkDeviceFleetEmployeeResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() =>
                this.appEvents.loaderVisiblePublish(false, 'report-admin-devices-by-employee'),
            ),
        );
    }

    exportDeviceFleet(payload: DeviceFleetExportRequest): Observable<Blob> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-devices-export');

        const sdkPayload: ReportSdk.AdminDeviceExportRequest = {
            format: payload.format as unknown as ReportSdk.ExportFormatEnum,
            ...(payload.employeeId != null ? { employeeId: payload.employeeId } : {}),
            ...(payload.search ? { search: payload.search } : {}),
            ...(payload.agentVersion ? { agentVersion: payload.agentVersion } : {}),
            ...(payload.deviceName ? { deviceName: payload.deviceName } : {}),
            ...(payload.includeDeleted != null ? { includeDeleted: payload.includeDeleted } : {}),
        };

        return this.adminService
            .exportAdminDeviceFleet(sdkPayload, 'response', false, {
                httpHeaderAccept: acceptFor(payload.format),
            } as never)
            .pipe(
                map((res) => this.httpResponseToBlob(res)),
                catchError((err) => {
                    this.showErrorToast(err);
                    return throwError(() => err);
                }),
                finalize(() =>
                    this.appEvents.loaderVisiblePublish(false, 'report-admin-devices-export'),
                ),
            );
    }

    private httpResponseToBlob(res: HttpResponse<unknown>): Blob {
        const body = res.body;
        if (body instanceof Blob) return body;
        const type = res.headers.get('Content-Type') ?? undefined;
        return new Blob(body != null ? [body as BlobPart] : [], { type });
    }

    deactivateDevice(deviceId: string): Observable<void> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-devices-delete');

        return this.adminService.deactivateAdminDevice(deviceId).pipe(
            map(() => void 0),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() =>
                this.appEvents.loaderVisiblePublish(false, 'report-admin-devices-delete'),
            ),
        );
    }

    reactivateDevice(deviceId: string): Observable<void> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-devices-reactivate');

        return this.adminService.reactivateAdminDevice(deviceId).pipe(
            map(() => void 0),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() =>
                this.appEvents.loaderVisiblePublish(false, 'report-admin-devices-reactivate'),
            ),
        );
    }

    private showErrorToast(err: unknown): void {
        if (!this.toast) return;
        const status = (err as { status?: number })?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            403: 'No tienes permiso para ver los dispositivos',
            // Cubre las dos direcciones: dar de baja algo ya dado de baja y
            // reactivar algo ya vigente responden 404 igual que si no existiera.
            404: 'El dispositivo no existe o ya cambió de estado',
            409: 'El colaborador ya alcanzó su máximo de dispositivos permitidos',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };
        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status ?? 0] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
